import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { apiService } from '../services/api';
import { apiErrorMessage } from '../utils/apiError';
import { MaterialGradeSummary } from '../types';

interface InternalOrderCertDialogProps {
  orderId: number;
  auftragsnummer: string;
  grades: MaterialGradeSummary[];
  open: boolean;
  onClose: () => void;
}

const InternalOrderCertDialog: React.FC<InternalOrderCertDialogProps> = ({
  orderId,
  auftragsnummer,
  grades,
  open,
  onClose,
}) => {
  const [vorlage, setVorlage] = useState<'kunde' | 'zoll_eu'>('kunde');
  const [format, setFormat] = useState<'pdf' | 'zip'>('pdf');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setVorlage('kunde');
    setFormat('pdf');
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { blob, filename } = await apiService.downloadInternalOrderCert(orderId, format, vorlage);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } catch (err: any) {
      const fallback = 'Fehler beim Erzeugen des Zeugnisses. Bitte erneut versuchen.';
      // Axios error body for blob responses needs special handling.
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          setError(apiErrorMessage({ response: { data: parsed } }, fallback));
        } catch {
          setError(fallback);
        }
      } else {
        setError(apiErrorMessage(err, fallback));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Zeugnis herunterladen für Auftrag {auftragsnummer}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>Vorlage</FormLabel>
              <RadioGroup
                value={vorlage}
                onChange={(e) => setVorlage(e.target.value as 'kunde' | 'zoll_eu')}
              >
                <FormControlLabel
                  value="kunde"
                  control={<Radio />}
                  label="Kundenzertifikat"
                  disabled={loading}
                />
                <FormControlLabel
                  value="zoll_eu"
                  control={<Radio />}
                  label="Zollmaterialzertifikat EU"
                  disabled={loading}
                />
              </RadioGroup>
              {vorlage === 'zoll_eu' && (
                <FormHelperText>
                  Schmelzland und Schmelznummer müssen an jedem Lieferanten Zertifikat des Auftrags stehen.
                </FormHelperText>
              )}
            </FormControl>
            <FormControl>
              <RadioGroup
                value={format}
                onChange={(e) => setFormat(e.target.value as 'pdf' | 'zip')}
              >
                <FormControlLabel
                  value="pdf"
                  control={<Radio />}
                  label="PDF — alle Zeugnisse zusammengefügt"
                  disabled={loading}
                />
                <FormControlLabel
                  value="zip"
                  control={<Radio />}
                  label="Excel-ZIP — eine .xlsx-Datei pro Charge"
                  disabled={loading}
                />
              </RadioGroup>
            </FormControl>
            {grades.length > 0 && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  Werkstoffe in diesem Auftrag:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {grades.map((g) => (
                    <Chip
                      key={g.material_grade_id}
                      size="small"
                      label={`${g.name} ×${g.coil_count}`}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleGenerate}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? 'Wird erstellt...' : 'Herunterladen'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={error !== null && !open}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {error !== null ? (
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default InternalOrderCertDialog;
